import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Reorder } from "framer-motion";
import { toast } from "sonner";
import api, { imgUrl } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import {
  Plus, Edit, Trash2, X, GripVertical, Upload, Eye, Image as ImageIcon,
  Save, Loader2, Mail, KeyRound, Link2, ShieldCheck, ShieldOff, RotateCw, ChevronDown,
} from "lucide-react";

const emptyForm = {
  title: "",
  platform: "Steam",
  description: "",
  image_url: "",
  steam_username: "",
  steam_password: "",
  gmail_address: "",
  gmail_app_password: "",
  code_source: "gmail_app_password",
  supplier_name: "sentinus",
  supplier_game_id: "",
  supplier_game_title: "",
  status: "available",
};

const CODE_SOURCES = [
  { value: "gmail_app_password", label: "Gmail App Password" },
  { value: "gmail_oauth", label: "Gmail OAuth 2.0" },
  { value: "supplier", label: "Supplier Website" },
];

export default function AdminProducts() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [modal, setModal] = useState(null);
  const [items, setItems] = useState([]);
  const [platforms, setPlatforms] = useState([{ key: "Steam", label: "Steam" }]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-products"],
    queryFn: async () => (await api.get("/products/admin/list")).data,
  });

  useEffect(() => {
    api.get("/products/platforms")
      .then((r) => setPlatforms(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (data) setItems(data);
  }, [data]);

  // Handle Gmail OAuth callback: URL contains ?gmail_oauth_ok=1 or ?gmail_oauth_error=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail_oauth_ok") === "1") {
      toast.success("Gmail OAuth connected");
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("gmail_oauth_error")) {
      toast.error(`Gmail OAuth: ${params.get("gmail_oauth_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [qc]);

  const onReorder = (newOrder) => {
    setItems(newOrder);
    api.post("/products/reorder", { ids: newOrder.map((i) => i.id) })
      .then(() => qc.invalidateQueries({ queryKey: ["admin-products"] }))
      .catch(() => toast.error("Reorder failed"));
  };

  const save = async (d) => {
    try {
      if (modal.mode === "create") {
        await api.post("/products", d);
        toast.success("Product added");
      } else {
        const payload = { ...d };
        if (!payload.steam_password) delete payload.steam_password;
        if (!payload.gmail_app_password) delete payload.gmail_app_password;
        await api.put(`/products/${d.id}`, payload);
        toast.success("Product updated");
      }
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.title}?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Deleted");
    qc.invalidateQueries({ queryKey: ["admin-products"] });
  };

  const reveal = async (p) => {
    try {
      const r = await api.get(`/products/${p.id}/reveal`);
      const dd = r.data;
      alert(
        `Source: ${dd.code_source}\n\n` +
        `Steam Username: ${dd.steam_username}\nSteam Password: ${dd.steam_password}\n` +
        `Gmail: ${dd.gmail_address}\nGmail App Password: ${dd.gmail_app_password}\n\n` +
        `Supplier: ${dd.supplier_name || "-"}\nSupplier Game: ${dd.supplier_game_title || dd.supplier_game_id || "-"}`
      );
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8" data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">Catalog & Accounts</div>
          <h1 className="font-heading text-3xl font-bold">{t("admin.products")}</h1>
          <p className="text-sm text-zinc-500 mt-1">Drag to reorder · click a row to view details</p>
        </div>
        <button
          onClick={() => setModal({ mode: "create", data: { ...emptyForm } })}
          className="bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-2.5 rounded-lg inline-flex items-center gap-2 glow-blue"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4" /> {t("common.add")}
        </button>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="bg-[#0c0c0c] border border-white/5 rounded-xl p-12 text-center text-zinc-500">{t("common.empty")}</div>
      ) : (
        <Reorder.Group axis="y" values={items} onReorder={onReorder} className="space-y-2" data-testid="products-list">
          {items.map((p) => (
            <Reorder.Item key={p.id} value={p} className="bg-[#0c0c0c] border border-white/5 rounded-xl p-4 hover:border-brand/30 transition-colors cursor-grab active:cursor-grabbing" data-testid={`product-row-${p.id}`}>
              <div className="flex items-center gap-4">
                <GripVertical className="w-5 h-5 text-zinc-600" />
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#080808] flex-shrink-0">
                  {p.image_url ? <img src={imgUrl(p.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-zinc-700 m-auto mt-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-heading font-bold truncate">{p.title}</span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30">{p.platform}</span>
                    <SourceBadge source={p.code_source} />
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                      p.status === "available" ? "bg-brand-green/10 text-brand-green border border-brand-green/30" :
                      "bg-white/5 text-zinc-500 border border-white/10"
                    }`}>{p.status === "delivered" ? "available" : p.status}</span>
                  </div>
                  <div className="text-xs text-zinc-500 font-mono truncate">
                    {p.steam_username || "—"} · {p.gmail_address || "—"}
                    {p.supplier_game_title ? ` · ${p.supplier_game_title}` : ""}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => reveal(p)} className="p-2 hover:bg-white/5 rounded text-zinc-400" title="Reveal" data-testid={`reveal-product-${p.id}`}><Eye className="w-4 h-4" /></button>
                  <button onClick={() => setModal({ mode: "edit", data: p })} className="p-2 hover:bg-white/5 rounded text-zinc-400" data-testid={`edit-product-${p.id}`}><Edit className="w-4 h-4" /></button>
                  <button onClick={() => del(p)} className="p-2 hover:bg-red-500/10 text-red-400 rounded" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}

      {modal && <ProductModal modal={modal} platforms={platforms} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function SourceBadge({ source }) {
  const map = {
    gmail_app_password: { label: "App PW", color: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" },
    gmail_oauth:       { label: "OAuth",   color: "text-sky-300 border-sky-500/30 bg-sky-500/10" },
    supplier:          { label: "Supplier", color: "text-amber-300 border-amber-500/30 bg-amber-500/10" },
  };
  const m = map[source] || map.gmail_app_password;
  return <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold border ${m.color}`}>{m.label}</span>;
}

function ProductModal({ modal, platforms, onClose, onSave }) {
  const [form, setForm] = useState(modal.data);
  const [uploading, setUploading] = useState(false);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [oauthBusy, setOauthBusy] = useState(false);
  const [supplierGames, setSupplierGames] = useState([]);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isEdit = modal.mode === "edit";
  const productId = form.id;

  // Load OAuth status when editing + source is oauth
  useEffect(() => {
    if (!isEdit || form.code_source !== "gmail_oauth" || !productId) return;
    api.get(`/gmail/oauth/status/${productId}`)
      .then((r) => setOauthStatus(r.data))
      .catch(() => setOauthStatus(null));
  }, [isEdit, productId, form.code_source]);

  // Load supplier games list when source is supplier
  useEffect(() => {
    if (form.code_source !== "supplier") return;
    api.get(`/suppliers/${form.supplier_name || "sentinus"}/games`)
      .then((r) => setSupplierGames(r.data || []))
      .catch(() => setSupplierGames([]));
  }, [form.code_source, form.supplier_name]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = localStorage.getItem("kha_admin_token") || "";
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const resp = await fetch(`${backend}/api/uploads/image`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body?.detail || `Upload failed (${resp.status})`);
      set("image_url", body.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const startOAuth = async () => {
    if (!productId) {
      toast.error("Save the product first, then connect Gmail OAuth.");
      return;
    }
    setOauthBusy(true);
    try {
      const r = await api.post(`/gmail/oauth/authorize/${productId}`);
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OAuth not configured");
    } finally {
      setOauthBusy(false);
    }
  };

  const disconnectOAuth = async () => {
    if (!productId) return;
    if (!window.confirm("Disconnect Gmail for this product?")) return;
    try {
      await api.post(`/gmail/oauth/disconnect/${productId}`);
      toast.success("Disconnected");
      setOauthStatus({ connected: false, status: "disconnected" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submit = (e) => {
    e?.preventDefault();
    if (!form.title?.trim()) return toast.error("Title is required");
    if (form.code_source === "gmail_app_password") {
      if (!form.steam_username?.trim() && form.platform === "Steam")
        return toast.error("Steam username required");
      if (!isEdit) {
        if (!form.gmail_address?.trim()) return toast.error("Gmail required");
        if (!form.gmail_app_password?.trim()) return toast.error("Gmail App Password required");
      }
    }
    if (form.code_source === "supplier") {
      // Multi-link mode: at least one link required, managed on the Suppliers page.
      if (isEdit && (!form.supplier_links || form.supplier_links.length === 0)) {
        return toast.error("Add at least one supplier account link on the Suppliers page.");
      }
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <form onSubmit={submit} className="bg-[#0c0c0c] border border-white/10 rounded-xl max-w-3xl w-full my-8" data-testid="product-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#0c0c0c] z-10">
          <h3 className="font-heading text-lg font-bold">{isEdit ? "Edit Game" : "Add Game"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          {/* Image */}
          <div className="col-span-2">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-bold">Game Image</div>
            <div className="flex items-center gap-4">
              <div className="w-28 h-28 rounded-lg overflow-hidden bg-[#080808] border border-white/5 flex-shrink-0">
                {form.image_url ? <img src={imgUrl(form.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-8 h-8 text-zinc-700 m-auto mt-9" />}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="image-upload-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-brand/10 hover:bg-brand/20 text-brand-cyan border border-brand/30 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="upload-image-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload Image
                </button>
                <p className="text-xs text-zinc-500 mt-2">PNG, JPG, WEBP. Max 5MB.</p>
              </div>
            </div>
          </div>

          {/* Game info */}
          <div className="col-span-2 grid grid-cols-2 gap-4 pt-2 border-t border-white/5">
            <Input label="Game Name *" value={form.title} onChange={(v) => set("title", v)} testid="product-title" />
            <Select label="Platform *"
                    value={form.platform}
                    options={platforms.map((p) => p.key)}
                    onChange={(v) => set("platform", v)}
                    testid="product-platform" />
            <Textarea label="Description (optional)" value={form.description} onChange={(v) => set("description", v)} testid="product-description" />
          </div>

          {/* Code Source picker */}
          <div className="col-span-2 pt-4 border-t border-white/5">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-3">Code Source</div>
            <div className="grid grid-cols-3 gap-2">
              {CODE_SOURCES.map((s) => (
                <button
                  type="button"
                  key={s.value}
                  onClick={() => set("code_source", s.value)}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-semibold text-start ${
                    form.code_source === s.value
                      ? "border-brand text-white bg-brand/10"
                      : "border-white/10 text-zinc-400 hover:border-white/20"
                  }`}
                  data-testid={`code-source-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Steam / Game credentials (still needed to actually deliver the game to the user) */}
          <div className="col-span-2 pt-4 border-t border-white/5">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-3">Account Credentials Delivered To User</div>
          </div>
          <Input label="Account Username" value={form.steam_username} onChange={(v) => set("steam_username", v)} testid="product-steam-user" />
          <Input label={isEdit ? "Account Password (leave blank to keep)" : "Account Password"} type="password" value={form.steam_password} onChange={(v) => set("steam_password", v)} testid="product-steam-pw" />

          {/* Source-specific settings */}
          {form.code_source === "gmail_app_password" && (
            <>
              <Input label="Gmail *" value={form.gmail_address} onChange={(v) => set("gmail_address", v)} testid="product-gmail" />
              <Input label={isEdit ? "Gmail App Password (leave blank to keep)" : "Gmail App Password *"} type="password" value={form.gmail_app_password} onChange={(v) => set("gmail_app_password", v)} testid="product-app-pw" />
              <div className="col-span-2 bg-brand/5 border border-brand/20 rounded p-3 text-xs text-zinc-400">
                <KeyRound className="w-3.5 h-3.5 inline me-1 text-brand-cyan" />
                Gmail App Password is a 16-character password from
                <code className="mx-1">myaccount.google.com/apppasswords</code>. 2-Step Verification must be ON.
              </div>
            </>
          )}

          {form.code_source === "gmail_oauth" && (
            <div className="col-span-2 bg-sky-500/5 border border-sky-500/20 rounded p-4">
              <div className="text-xs uppercase tracking-wider text-sky-300 mb-2 font-bold flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Gmail OAuth 2.0
              </div>
              {!isEdit ? (
                <p className="text-sm text-zinc-400">Save the product first, then reopen it to Connect Gmail.</p>
              ) : (
                <OAuthPanel
                  status={oauthStatus}
                  onConnect={startOAuth}
                  onDisconnect={disconnectOAuth}
                  busy={oauthBusy}
                />
              )}
            </div>
          )}

          {form.code_source === "supplier" && (
            <div className="col-span-2 bg-amber-500/5 border border-amber-500/20 rounded p-4 space-y-3">
              <div className="text-xs uppercase tracking-wider text-amber-300 mb-2 font-bold flex items-center gap-2">
                <Link2 className="w-3.5 h-3.5" /> Supplier Accounts
              </div>
              {isEdit && form.supplier_links && form.supplier_links.length > 0 ? (
                <div className="space-y-1.5">
                  {form.supplier_links.map((l, i) => (
                    <div key={i} className="text-xs bg-[#080808] border border-white/5 rounded px-3 py-2 flex items-center justify-between">
                      <span className="font-mono truncate">{l.supplier_name || "sentinus"} · {l.supplier_game_title || l.supplier_game_id}</span>
                      <span className="text-zinc-500">acc {(l.account_id || "").slice(0, 8)}</span>
                    </div>
                  ))}
                  <p className="text-xs text-zinc-500 pt-2">To add / remove links, open the <strong>Suppliers</strong> page — you can link this product to games from any supplier account there.</p>
                </div>
              ) : (
                <p className="text-sm text-zinc-400">
                  Save the product first, then open the <strong>Suppliers</strong> page and pick a game to link.
                  A product can be linked to multiple supplier accounts — the system will try them in order.
                </p>
              )}
            </div>
          )}

          {isEdit && (
            <Select label="Status" value={form.status} options={["available", "disabled"]} onChange={(v) => set("status", v)} testid="product-status" />
          )}
        </div>
        <div className="px-6 py-4 border-t border-white/5 flex justify-end gap-2 sticky bottom-0 bg-[#0c0c0c]">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-white/5 hover:bg-white/10">Cancel</button>
          <button type="submit" className="px-5 py-2 rounded-md bg-brand hover:bg-brand-hover font-semibold inline-flex items-center gap-2" data-testid="product-save-btn">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </form>
    </div>
  );
}

function OAuthPanel({ status, onConnect, onDisconnect, busy }) {
  const connected = status?.connected;
  const state = status?.status || "disconnected";
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        {connected ? (
          <>
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 font-semibold">Connected</span>
            {status?.email && <span className="text-zinc-400 truncate">as {status.email}</span>}
          </>
        ) : (
          <>
            <ShieldOff className="w-4 h-4 text-zinc-500" />
            <span className="text-zinc-400 font-semibold capitalize">{state}</span>
          </>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        {!connected && (
          <button
            type="button" onClick={onConnect} disabled={busy}
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg text-sm"
            data-testid="gmail-oauth-connect-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Connect Gmail
          </button>
        )}
        {connected && (
          <>
            <button
              type="button" onClick={onConnect} disabled={busy}
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold px-4 py-2 rounded-lg text-sm"
              data-testid="gmail-oauth-reconnect-btn"
            >
              <RotateCw className="w-4 h-4" /> Reconnect
            </button>
            <button
              type="button" onClick={onDisconnect}
              className="inline-flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 font-semibold px-4 py-2 rounded-lg text-sm"
              data-testid="gmail-oauth-disconnect-btn"
            >
              <ShieldOff className="w-4 h-4" /> Disconnect
            </button>
          </>
        )}
      </div>
      <p className="text-xs text-zinc-500">
        Access &amp; refresh tokens are stored encrypted on the server and are never sent to the browser.
      </p>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 font-bold">{label}</div>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#080808] border border-white/10 rounded px-3 py-2 focus:border-brand outline-none" data-testid={testid} />
    </div>
  );
}
function Select({ label, value, options, onChange, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 font-bold">{label}</div>
      <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full appearance-none bg-[#080808] border border-white/10 rounded px-3 py-2 focus:border-brand outline-none pr-8" data-testid={testid}>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown className="w-4 h-4 text-zinc-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
    </div>
  );
}
function Textarea({ label, value, onChange, testid }) {
  return (
    <div className="col-span-2">
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 font-bold">{label}</div>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} className="w-full bg-[#080808] border border-white/10 rounded px-3 py-2 focus:border-brand outline-none resize-none" data-testid={testid} />
    </div>
  );
}
